import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Apollo } from 'apollo-angular';

import { HeaderComponent } from '@components/dashboard/header/header.component';
import { NavComponent } from '@components/dashboard/navigation/nav.component';
import { BaseCoreUtils } from '@core/base-core-util';
import { ModuleNames } from '@enums/module-names';
import { PARTNER_SUMMARY } from '@gql_queries/partners/queries';
import { AuthStore } from '@store/auth.store';
import { isUnsupportedByApi } from '@shared/utils/graphql-support.util';
import {
    PARTNER_SERVICE_LABELS,
    PARTNER_VEHICLE_CLASS_ICONS,
    PARTNER_VEHICLE_CLASS_LABELS,
    PARTNER_VEHICLE_MIX_LABELS,
    PartnerVehicleClass,
} from '@shared/utils/partner-presentation.util';

interface ServiceCard {
    key: string;
    vehicleClass: PartnerVehicleClass;
    count: number;
    shareOfApproved: number;
}

interface VehicleCard {
    vehicleClass: PartnerVehicleClass;
    total: number;
    rows: { key: string; count: number }[];
}

/**
 * Partners > Summary: where every registered partner sits in onboarding, and
 * what the approved ones are approved for.
 *
 * Two different kinds of number live on this page and the copy says so: the
 * by-service counts overlap (a partner can hold several services), while the
 * by-vehicle counts are a headcount that adds up to the approved total.
 */
@Component({
    selector: 'app-dashboard-partners-summary',
    standalone: true,
    templateUrl: './dashboard-partners-summary.component.html',
    imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule],
})
export class DashboardPartnersSummaryComponent {
    private coreUtils!: BaseCoreUtils;

    constructor(
        private router: Router,
        private apollo: Apollo,
        private authStore: AuthStore,
    ) {
        this.coreUtils = new BaseCoreUtils({
            module: ModuleNames.PARTNERS,
            apollo: this.apollo,
            router: this.router,
            authStore: this.authStore,
        });
    }

    public asOf: string = new Date().toISOString().slice(0, 10);
    public isLoading = true;
    public loadFailed = false;
    /** The API predates `partnerSummary`, rather than the request failing. */
    public apiUnsupported = false;

    public pipeline: Record<string, number> = {
        totalRegistrations: 0,
        underReview: 0,
        withDocuments: 0,
        forAmendment: 0,
        approved: 0,
    };

    public byService: ServiceCard[] = [];
    public byVehicle: VehicleCard[] = [];

    /** Pipeline cards, in the order onboarding runs. */
    public readonly pipelineCards = [
        {
            key: 'totalRegistrations',
            label: 'Total registrations',
            icon: 'registered.svg',
            hint: 'Everyone who has created a partner account, at any stage. The four stages beside it are mutually exclusive and cover every status, so they add up to this number.',
        },
        {
            key: 'underReview',
            label: 'Under review',
            icon: 'verification.svg',
            hint: 'Submitted a complete document set and waiting for the Admin team to verify it.',
        },
        {
            key: 'withDocuments',
            label: 'With documents',
            icon: 'resubmission.svg',
            hint: "Uploaded some documents but haven't submitted the full set for review yet.",
        },
        {
            key: 'forAmendment',
            label: 'For amendment',
            icon: 'amendment.svg',
            hint: 'Reviewed and sent back because a document was missing, unclear, or expired. The partner needs to re-upload it.',
        },
        {
            key: 'approved',
            label: 'Approved',
            icon: 'approved.svg',
            hint: 'Partners who passed document verification and can accept bookings in at least one service. Each partner is counted once.',
            active: true,
        },
    ];

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.isLoading = true;
        this.loadFailed = false;
        this.apiUnsupported = false;

        this.coreUtils.overview(PARTNER_SUMMARY, { asOf: this.asOf }, (data) => {
            const summary = data?.['partnerSummary'];
            this.isLoading = false;

            if (!summary) {
                this.loadFailed = true;
                return;
            }

            Object.keys(this.pipeline).forEach((key) => {
                this.pipeline[key] = summary.pipeline?.[key] ?? 0;
            });
            this.byService = summary.byService ?? [];
            this.byVehicle = summary.byVehicle ?? [];
        }, (error) => {
            // Without this the page kept its initial zeros and looked like a
            // fleet with no partners at all, which is the one reading worse
            // than an error.
            this.isLoading = false;
            this.loadFailed = true;
            this.apiUnsupported = isUnsupportedByApi(error);
        });
    }

    reload(event: Event): void {
        event.preventDefault();
        this.load();
    }

    onAsOfChange(): void {
        if (!this.asOf) return;
        this.load();
    }

    /** "As of October 4, 2026" — the date the counts were taken for. */
    get asOfLabel(): string {
        const parsed = new Date(`${this.asOf}T00:00:00`);
        if (Number.isNaN(parsed.getTime())) return this.asOf;
        return parsed.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
    }

    serviceName(key: string): string {
        return PARTNER_SERVICE_LABELS[key]?.name ?? key;
    }

    serviceSubtitle(key: string): string {
        return PARTNER_SERVICE_LABELS[key]?.subtitle ?? '';
    }

    serviceIcon(key: string): string {
        return PARTNER_SERVICE_LABELS[key]?.icon ?? 'express-icon.png';
    }

    vehicleLabel(vehicleClass: PartnerVehicleClass): string {
        return PARTNER_VEHICLE_CLASS_LABELS[vehicleClass] ?? vehicleClass;
    }

    vehicleIcon(vehicleClass: PartnerVehicleClass): string {
        return PARTNER_VEHICLE_CLASS_ICONS[vehicleClass] ?? 'riders.svg';
    }

    mixLabel(key: string): string {
        return PARTNER_VEHICLE_MIX_LABELS[key]?.label ?? key;
    }

    mixDot(key: string): string {
        return PARTNER_VEHICLE_MIX_LABELS[key]?.dot ?? 'veh-dot-express';
    }
}
